/**
 * Product Info Sidebar - Sticky Position Handler
 * Handles sticky positioning of right sidebar with dynamic header height tracking
 * Similar to cart-popup.js implementation
 * 
 * @version 1.1.0
 * @author Fade Shopify Team
 */

class ProductInfoSidebar extends HTMLElement {
  constructor() {
    super();
    
    this.headerObserver = null;
    this.sidebar = null;
    this.header = null;
  }

  connectedCallback() {
    // Find sidebar element
    this.sidebar = this.querySelector('.product-info-right--sticky');
    
    if (!this.sidebar) {
      console.warn('Product info sidebar: .product-info-right--sticky not found');
      return;
    }
    
    // Find header with multiple fallbacks
    this.header = this.findHeader();
    
    if (!this.header) {
      console.warn('Product info sidebar: Header element not found');
      // Set default offset if header not found
      this.sidebar.style.top = '100px';
      return;
    }
    
    console.log('Product info sidebar: Found header', this.header);
    this.initHeaderTracking();
  }

  disconnectedCallback() {
    this.cleanupHeaderTracking();
  }

  /**
   * Find header element with multiple selector fallbacks
   */
  findHeader() {
    const selectors = [
      'sticky-header',
      '.header-wrapper',
      'header.header',
      'header',
      '.shopify-section-header',
      '#shopify-section-header'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`Product info sidebar: Header found with selector "${selector}"`);
        return element;
      }
    }
    
    return null;
  }

  /**
   * Initialize header height tracking
   * Automatically updates sidebar sticky position based on header height
   */
  initHeaderTracking() {
    // Initial update
    this.updateSidebarPosition();
    
    // Track header height changes using ResizeObserver
    if (this.header && window.ResizeObserver) {
      this.headerObserver = new ResizeObserver(() => {
        this.updateSidebarPosition();
      });
      this.headerObserver.observe(this.header);
      console.log('Product info sidebar: ResizeObserver attached to header');
    }
    
    // Also update on scroll (for sticky header transitions)
    this.boundHandleScroll = this.handleScroll.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
    
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
    window.addEventListener('resize', this.boundHandleResize, { passive: true });
    
    console.log('Product info sidebar: Event listeners attached');
  }

  /**
   * Cleanup header tracking observers
   */
  cleanupHeaderTracking() {
    if (this.headerObserver) {
      this.headerObserver.disconnect();
      this.headerObserver = null;
    }
    
    if (this.boundHandleScroll) {
      window.removeEventListener('scroll', this.boundHandleScroll);
    }
    
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }
  }

  /**
   * Update sidebar sticky top position based on actual header height
   */
  updateSidebarPosition() {
    if (!this.sidebar || !this.header) return;
    
    const headerHeight = this.header.offsetHeight;
    const viewportHeight = window.innerHeight;
    const maxHeight = viewportHeight - headerHeight - 20; // 20px buffer
    
    // Update styles
    this.sidebar.style.top = `${headerHeight}px`;
    this.sidebar.style.maxHeight = `${maxHeight}px`;
    
    console.log(`Product info sidebar: Positioned at top: ${headerHeight}px, maxHeight: ${maxHeight}px`);
  }

  /**
   * Handle scroll events (for sticky header transitions)
   */
  handleScroll() {
    this.updateSidebarPosition();
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    this.updateSidebarPosition();
  }
}

// Register custom element
if (!customElements.get('product-info-sidebar')) {
  customElements.define('product-info-sidebar', ProductInfoSidebar);
  console.log('Product info sidebar: Custom element registered');
}

// Initialize immediately and on DOM ready
function initProductInfoSidebar() {
  const sidebar = document.querySelector('.product-info-right--sticky');
  
  if (!sidebar) {
    console.warn('Product info sidebar: .product-info-right--sticky not found');
    return;
  }
  
  if (sidebar.dataset.initialized === 'true') {
    console.log('Product info sidebar: Already initialized');
    return;
  }
  
  // Find header
  const headerSelectors = [
    'sticky-header',
    '.header-wrapper',
    'header.header',
    'header',
    '.shopify-section-header',
    '#shopify-section-header'
  ];
  
  let header = null;
  for (const selector of headerSelectors) {
    header = document.querySelector(selector);
    if (header) {
      console.log(`Product info sidebar: Header found with selector "${selector}"`);
      break;
    }
  }
  
  if (!header) {
    console.warn('Product info sidebar: Header element not found');
    sidebar.style.top = '0px';
    return;
  }
  
  // Update position function
  function updateSidebarPosition() {
    const headerHeight = header.offsetHeight;
    const viewportHeight = window.innerHeight;
    const maxHeight = viewportHeight - headerHeight - 20;
    
    sidebar.style.top = `${headerHeight}px`;
    sidebar.style.maxHeight = `${maxHeight}px`;
    
    console.log(`Product info sidebar: Positioned at top: ${headerHeight}px, maxHeight: ${maxHeight}px`);
  }
  
  // Initial update
  updateSidebarPosition();
  
  // Track header height changes
  if (window.ResizeObserver) {
    const observer = new ResizeObserver(updateSidebarPosition);
    observer.observe(header);
    console.log('Product info sidebar: ResizeObserver attached');
  }
  
  // Event listeners
  window.addEventListener('scroll', updateSidebarPosition, { passive: true });
  window.addEventListener('resize', updateSidebarPosition, { passive: true });
  
  sidebar.dataset.initialized = 'true';
  console.log('Product info sidebar: Initialized successfully');
}

// Try to initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductInfoSidebar);
} else {
  // DOM already loaded, initialize immediately
  initProductInfoSidebar();
}

// Also try after a short delay as fallback
setTimeout(initProductInfoSidebar, 500);
